import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import { loadScript, mergeObjects, pushTrack } from "utils";
import type { InstantConfigRepository } from "instant-config";
import { type FandomCmp, LoadTimeEvent } from "common-types";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";
import { PrebidPlugin } from "../../../PrebidPlugin/PrebidPlugin";
import { STANDARD_PREBID_ADSERVER_TARGETING_CONFIG } from "../../../../config/prebid";

type BlockthroughReadershipLinkPluginConfig = Pick<
  InstantConfigRepository,
  "icBlockthroughReadershipLink"
>;

/**
 * BlockthroughReadershipLinkPlugin is a plugin that loads the Blockthrough Readership Link script.
 *
 * Note that the plugin will load the script even if the user has opted out of
 * personalized ads, but it will load the script *regardles* of ad blocking. This is
 * a requirement for the Blockthrough Readership Link script.
 * After script is loaded, it will be executed immediately. At that point, the
 * Blockthrough Readership Link code will execute and do its things.
 *
 * The plugin is controlled by the `icBlockthroughReadershipLink` property in the
 * InstantConfig. If this property is set to true, the plugin will be enabled and
 * the script will be loaded. If this property is set to false, the plugin will be
 * disabled and the script will not be loaded.
 *
 * The plugin will only load the script once, even if the user opts in and out of
 * personalized ads multiple times.
 *
 * @link https://services.fandom.com/icbm/dashboard/feature_flag/icBlockthroughReadershipLink
 */
export class BlockthroughReadershipLinkPlugin extends AdEnginePlugin {
  // The script below should load 'https://cdn.btmessage.com/script/rlink.js?o=5199505043488768';
  static readonly SCRIPT_URL =
    "https://btloader.com/tag?o=5199505043488768&upapi=true";
  private isInitialized = false;

  /**
   * Checks if the plugin should be enabled on this page using the InstantConfig `icBlockthroughReadershipLink`
   * property
   * @param instantConfig The instance config objet with the `icBlockthroughReadershipLink` property to see if the
   * plugin is enabled on the page
   * @returns True if the plugin is enabled, false otherwise
   */
  static isEnabled({
    icBlockthroughReadershipLink,
  }: BlockthroughReadershipLinkPluginConfig): boolean {
    return !!icBlockthroughReadershipLink;
  }

  /**
   * Blockthrough will look for softwall advertising (for example
   * [AdBlock Acceptable Ads program and "non-intrusive" ads](https://helpcenter.getadblock.com/hc/en-us/articles/9738480686483-About-the-Acceptable-Ads-program-and-non-intrusive-ads)),
   * and when detected it will add the `rl_wid` and `bt_mwr` key-values which
   * are used to still load ads. This is done by the script loaded from
   * `btmessage.com` (which is loaded by the {@link SCRIPT_URL} in this plugin),
   * which modifies the `pbjs.bidderSettings.standard.adserverTargeting`
   * configuration to apply those two key-values. However, this will switch the
   * `adserverTargeting` to go into full manual mode instead of applying the
   * default standard configuration which drops some critical key-values such as
   * `hb_adid` and `hb_source`.
   *
   * So when Blockthrough is enabled, we will need to manually add the standard
   * adserver targeting configuration ourselves to ensure all the expected
   * key-values are set correctly. This configuration can be found in the {@link
   * STANDARD_PREBID_ADSERVER_TARGETING_CONFIG} constant initialized in the
   * `prebid.ts` config file. The configuration is _merged_ so any other bidder
   * settings that are already configuration will not be overwritten.
   *
   * @see https://docs.prebid.org/dev-docs/publisher-api-reference/bidderSettings.html#21-adservertargeting
   */
  private adjustPrebidAdserverTargetingConfig(): void {
    this.usePlugin(PrebidPlugin)?.addPrebidReadyCallback((pbjs) => {
      pbjs.bidderSettings = mergeObjects(
        pbjs.bidderSettings,
        STANDARD_PREBID_ADSERVER_TARGETING_CONFIG,
      ) as PrebidBidderSettings;
    });
  }

  /**
   * Initializes the BlockthroughReadershipLink script by adding it to the page. Ensures that the script is only
   * loaded once, even if the method is called multiple times.
   * @returns void
   */
  private async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    // load script and do not care about the result
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "bt-loader_started",
        time: performance.now(),
      });

      this.adjustPrebidAdserverTargetingConfig();

      await loadScript(BlockthroughReadershipLinkPlugin.SCRIPT_URL);

      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "bt-loader_loaded",
        time: performance.now(),
      });
    } catch (error) {
      console.warn("Failed to load BlockthroughReadershipLink script", error);
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "bt-loader_error_loading",
        time: performance.now(),
      });
    }
  }

  hooks: Hooks = {
    /**
     * Hook that runs when consent is updated.
     * @param {FandomCmp} fandomCmp - The FandomCmp instance containing consent information.
     */
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.initialize();
    },
  };
}
